import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { AxiosError } from "axios";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useNotify } from "../../../../context/NotificationContext";
import { FEE_APIS } from "../../api/apis";
import { get, getAxiosHeaders, printReceipt } from "../../shared/AxiosAPI";
import { MaterialUITable } from "../../shared/MaterialUITable";
import { TableActions } from "../../shared/TableActions";
import PageTopBar from "../../../../components/common/PageTopBar";
import { toolbarButtonSx, toolbarFieldsSx } from "../../../../components/common/toolbarStyles";
import { Fee, FeePage, FeeReceiptListItem, MaterialUITableDataItem, UserRole } from "../../shared/types";
import { exportToExcel, getStaticGrades, getStaticSections } from "../../shared/Util";
import { getLoggedInUserAdmissionNo, getLoggedInUserRole } from "../../user/UserService";

import FileDownloadIcon from "@mui/icons-material/FileDownload";

export namespace View {
  export const NOT_AVAILABLE: string = "NOT_AVAILABLE";
}

export const FeeReport = () => {
  const notify = useNotify();
  const [feeList, setFeeList] = useState<Fee[]>([]);
  const [feeReceiptList, setFeeReceiptList] = useState<FeeReceiptListItem[]>([]);
  const [feeListLoaded, setFeeListLoaded] = useState<boolean>(false);
  const [studentFeeListLoaded, setStudentFeeListLoaded] = useState<boolean>(false);
  const [view, setView] = useState<string>();
  const [studentFeesData, setStudentFeesData] = useState<MaterialUITableDataItem[]>();
  const [dateIsRequired, setDateIsRequired] = useState<boolean>(false);
  const [isAPICallReq, setIsAPICallReq] = useState<boolean>(false);
  // admin: message shown in the page when a Get finds nothing (null before the first Get / with results)
  const [noResultText, setNoResultText] = useState<string | null>(null);

  //Refs
  const selectGradeRef = useRef<HTMLInputElement>();
  const selectSectionRef = useRef<HTMLInputElement>();
  const date1Ref = useRef<HTMLInputElement>();
  const date2Ref = useRef<HTMLInputElement>();

  //Pagination
  const [page, setPage] = useState(0);
  const [totalElements, setTotalElements] = useState(0);

  useEffect(() => {
    if (getLoggedInUserRole() === UserRole.STUDENT) {
      getStudentFeeReceipts();
    }
  }, []);

  const [studentFDsSetInterval, setStudentFDsSetInterval] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => {
      console.log("Getting fee receipts");
      if (getLoggedInUserRole() === UserRole.STUDENT) {
        getStudentFeeReceipts();
        setStudentFDsSetInterval(studentFDsSetInterval + 1);
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [studentFDsSetInterval]);

  const setMUITableData = (fees: Fee[]) => {
    if (fees.length > 0) {
      //Material Data Table Data
      let stdFeeDueData: MaterialUITableDataItem[] = fees.map((fee, index) => ({
        "Grade-Sec": fee.gradeName + "-" + fee.sectionName,
        Term: fee.terms,
        Amount: fee.amount,
        Actions: (
          <Link
            to="#"
            onClick={(e) => {
              printReceipt(Number.parseInt(fee.receiptNo));
            }}
          >
            Print
          </Link>
        ),
      }));
      setStudentFeesData(stdFeeDueData);
    } else {
      setView(View.NOT_AVAILABLE);
    }
  };

  const getStudentFeeReceipts = () => {
    let cHeaders = getAxiosHeaders();
    cHeaders.set("admissionNo", getLoggedInUserAdmissionNo());
    get(FEE_APIS.GET_FEE_LIST_BY_STUDENTS, cHeaders)
      .then((res) => {
        let feeList: Fee[] = res.data;
        if (feeList.length > 0) {
          setFeeList(feeList);
          setStudentFeeListLoaded(true);
          setMUITableData(feeList);
        } else {
          setStudentFeeListLoaded(false);
          setView(View.NOT_AVAILABLE);
        }
      })
      .catch((err: AxiosError) => {
        setStudentFeeListLoaded(false);
      });
  };

  const gradeFeesForAdmin = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setFeeListLoaded(false);
    setFeeReceiptList(undefined); // clears state
    if (page !== 0) {
      setPage(0);
      setRowsPerPage(5);
    } else {
      updateFeePage();
    }

  };

  const updateFeePage = () => {
    setFeeListLoaded(false);
    setFeeReceiptList(undefined); // clears state

    let receiptDate1 = date1Ref.current?.value;

    if (receiptDate1 === undefined || receiptDate1 === null || receiptDate1 === "") {
      setDateIsRequired(true);
      return;
    } else {
      setDateIsRequired(false);
    }

    const url = getFeeReceiptsUrl("list", { page, size: rowsPerPage });

    get(url, getAxiosHeaders())
      .then((res) => {
        let feePage: FeePage = res.data;
        let newFeeList = feePage.feeList;
        setFeeReceiptList([...newFeeList]);
        setTotalElements(feePage.totalElements);
        if (newFeeList.length > 0) {
          setFeeListLoaded(true);
          setNoResultText(null);
        } else {
          setFeeListLoaded(false);
          setNoResultText(noFeesMessage()); // shown in the page
          notifyNoFees();
        }
      })
      .catch((err) => {
        alert(err);
        setFeeListLoaded(false);
      });
  };

  // GET /fee?view=xl|list&grade=&section=&date1=&date2=, grade and section are only sent when selected, otherwise all are returned
  const getFeeReceiptsUrl = (view: "xl" | "list", extraParams: Record<string, string | number> = {}): string => {
    const query = new URLSearchParams({ view, date1: date1Ref.current?.value ?? "" });
    const grade = selectGradeRef.current?.value;
    const section = selectSectionRef.current?.value;
    if (grade) query.set("grade", grade);
    if (section) query.set("section", section);
    const receiptDate2 = date2Ref.current?.value;
    if (receiptDate2) query.set("date2", receiptDate2);
    Object.entries(extraParams).forEach(([key, value]) => query.set(key, String(value)));
    return FEE_APIS.GET_FEE_RECEIPTS + "?" + query.toString();
  };

  /** "No fee receipts found for grade Five, section A, receipt date 30-09-2026" */
  const noFeesMessage = (): string => {
    const grade = selectGradeRef.current?.value;
    const section = selectSectionRef.current?.value;
    const receiptDate1 = date1Ref.current?.value;
    const receiptDate2 = date2Ref.current?.value;

    const criteria: string[] = [];
    if (grade) criteria.push("grade " + grade);
    if (section) criteria.push("section " + section);
    criteria.push(receiptDate2 ? `receipt dates ${receiptDate1} to ${receiptDate2}` : `receipt date ${receiptDate1}`);
    return "No fee receipts found for " + criteria.join(", ") + ".";
  };

  const notifyNoFees = () => notify(noFeesMessage(), "info");

  //MUI
  const [rowsPerPage, setRowsPerPage] = useState<number>(5);
  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to the first page
  };

  //TODO it should not runon the first run ...
  useEffect(() => {
    if (isAPICallReq) {
      updateFeePage();
    } else {
      setIsAPICallReq(true); // Skip the first run
    }
  }, [page, rowsPerPage]);

  const downloadAsExcel = () => {
    let receiptDate1 = date1Ref.current?.value;
    if (receiptDate1 === undefined || receiptDate1 === null || receiptDate1 === "") {
      alert("receiptDate1 cannot be null");
      return;
    }
    get(getFeeReceiptsUrl("xl"), getAxiosHeaders())
      .then((res) => {
        if (res.data.length === 0) {
          notifyNoFees();
          return;
        }
        // The backend sends the rows with the Excel column names, in the column order
        exportToExcel(res.data, "fee-list-" + receiptDate1);
      })
      .catch((err) => {
        alert(err);
        setFeeListLoaded(false);
      });
  };
  if (getLoggedInUserRole() === UserRole.ADMIN) {
    return (
      <Box sx={{ mt: 2, mr: 2 }}>
        <PageTopBar
          left="Receipts"
          right={
        <Box
          component="form"
          sx={{ ...toolbarFieldsSx, display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}
          onSubmit={gradeFeesForAdmin}
        >
          <FormControl sx={{ minWidth: 110 }} size="small">
            <InputLabel id="grade-label">Grade</InputLabel>
            <Select
              labelId="grade-select-label"
              id="grade-select"
              name="grade"
              label="Grade"
              defaultValue={""}
              inputRef={selectGradeRef}
            >
              {getStaticGrades()?.map((grade: string) => (
                <MenuItem value={grade} key={grade}>
                  {grade}
                </MenuItem>
              ))}
              <MenuItem value={"Graduate"} key={"Graduate"}>
                {"Graduate"}
              </MenuItem>
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 100 }} size="small">
            <InputLabel id="section-label">Section</InputLabel>
            <Select
              labelId="section-select-label"
              id="section-select"
              name="section"
              label="Section"
              defaultValue={""}
              inputRef={selectSectionRef}
            >
              {getStaticSections()?.map((section: string) => (
                <MenuItem value={section} key={section}>
                  {section}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <DatePicker
            label="From *"
            defaultValue={dayjs()}
            onChange={(newValue) => {
              if (newValue !== undefined && newValue?.valueOf().toString() !== "") setDateIsRequired(false);
              else setDateIsRequired(true);
            }}
            slotProps={{
              textField: (params) => ({
                ...params,
                name: "receiptDate1",
                size: "small",
                // required: a missing date turns the field red (no helper text, so the bar keeps its height)
                error: dateIsRequired,
                inputRef: date1Ref,
              }),
            }}
            sx={{ width: "150px" }}
            format="DD-MM-YYYY"
          />
          <DatePicker
            label="To"
            slotProps={{
              textField: { size: "small", inputRef: date2Ref },
            }}
            sx={{ width: "150px" }}
            format="DD-MM-YYYY"
            name="receiptDate2"
          />

          <Button type="submit" variant="outlined" size="small" sx={toolbarButtonSx}>
            Get
          </Button>
          <Button variant="outlined" size="small" sx={toolbarButtonSx} startIcon={<FileDownloadIcon />} onClick={downloadAsExcel}>
            Excel
          </Button>
        </Box>
          }
        />
        {!feeListLoaded && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
            {noResultText ?? "Pick a receipt date (and a grade / section if needed), then click Get."}
          </Typography>
        )}
        {feeListLoaded ? (
          <>
            <TableContainer className="mui-table-container-style">
              <Table size="small" className="mui-table-bordered">
                <TableHead>
                  <TableRow>
                    <TableCell>Recpt #</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Admission No</TableCell>
                    <TableCell>Grade-Sec</TableCell>
                    <TableCell>Terms</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Discount</TableCell>
                    <TableCell>Payment Mode</TableCell>
                    <TableCell>Transaction ID</TableCell>
                    <TableCell>Date of Payment</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {feeReceiptList.map((fee: FeeReceiptListItem) => (
                    <TableRow key={fee.admissionNo + fee.receiptNo}>
                      <TableCell>{fee.receiptNo}</TableCell>
                      <TableCell>{fee.studentName}</TableCell>
                      <TableCell>{fee.admissionNo}</TableCell>
                      <TableCell>
                        {fee.gradeName} - {fee.sectionName}
                      </TableCell>
                      <TableCell>{fee.terms}</TableCell>
                      <TableCell>{fee.amount}</TableCell>
                      <TableCell>{fee.discount !== 0 ? fee.discount : undefined}</TableCell>
                      <TableCell>{fee.paymentMode}</TableCell>
                      <TableCell>{fee.transactionId}</TableCell>
                      <TableCell>{fee.dateOfPayment}</TableCell>
                      <TableCell>
                        <Link
                          to="#"
                          onClick={(e) => {
                            printReceipt(Number(fee.receiptNo));
                          }}
                        >
                          Print
                        </Link>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={totalElements} // NOTE: this should be total number of items, not pages
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25]}
                size="small"
                sx={{
                  "& .MuiTablePagination-toolbar": {
                    justifyContent: "space-between",
                    alignItems: "center",
                    paddingLeft: 2,
                    paddingRight: 2,
                  },
                }}
              />
            </TableContainer>
          </>
        ) : (
          ""
        )}
      </Box>
    );
  } else if (getLoggedInUserRole() === UserRole.STUDENT) {
    return (
      <>
        <Box>
          {studentFeeListLoaded ? (
            <>
              <TableActions
                fileName={"fee-list-bystudent-report-"}
                data={feeList}
                heading="Receipts"
              />
              <MaterialUITable data={studentFeesData} />
            </>
          ) : (
            ""
          )}
        </Box>

        {view === View.NOT_AVAILABLE ? <p>FEE_RECEIPTS_NOT_AVAILABLE</p> : ""}
      </>
    );
  }
};
